import { SourcingStats } from 'api-clients/monolith';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { Typography, TypographyVariant } from '@/components/ui/typography';
import { useHireUrl } from '@/hooks/use-hire-url';
import { getProgress } from '@/utils/progress';

import { Button } from './button';
import { Progress } from './progress';
import { Tooltip } from './tooltip';

interface DurationProps {
  stats: SourcingStats;
  brand?: string;
  location?: string;
  funnel?: string;
  showProgress?: boolean;
  typographyVariant?: TypographyVariant;
  showDays?: boolean;
  showDateRange?: boolean;
  startDate?: string;
  endDate?: string;
  tooltipContent?: ReactNode;
}

export function Duration({
  stats,
  brand,
  location,
  funnel,
  showProgress = false,
  typographyVariant = 'text',
  showDays = true,
  showDateRange = false,
  startDate,
  endDate,
  tooltipContent,
}: DurationProps) {
  const { getHireATHiringGoalUrl } = useHireUrl();
  const { expected, actual } = stats;
  const progress = getProgress(expected, actual);

  if (!expected && !actual)
    return (
      <Button variant="link" asChild>
        <Link
          to={getHireATHiringGoalUrl(brand ?? 'no_brand', location, funnel)}
          target="_blank"
        >
          <FormattedMessage defaultMessage="+ Add hiring goal" />
        </Link>
      </Button>
    );

  const renderContent = () => {
    if (showDateRange && startDate && endDate) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
    }

    if (showDays) {
      return (
        <FormattedMessage
          defaultMessage="{actual} / {expected} days"
          values={{ actual: actual ?? '-', expected: expected ?? '-' }}
        />
      );
    }

    return `${actual ?? '-'} / ${expected ?? '-'}`;
  };

  return (
    <>
      <Tooltip content={tooltipContent}>
        <Typography variant={typographyVariant}>{renderContent()}</Typography>
      </Tooltip>
      {showProgress && <Progress value={progress} />}
    </>
  );
}
